import { ChangeDetectionStrategy, Component, OnInit, inject } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { InfoComponent } from "@components/info/info.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { LabelComponent } from "@components/label/label.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { NgbActiveModal, NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { chain, differenceBy, sumBy } from "lodash";
import { Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { CommandService } from "~app/common/services/cqs/command.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { ValidationErrorLevels } from "~app/common/validation/ValidationErrorLevels";
import { ValidationContext } from "~app/common/validation/validationContext";
import { MergeAllDestineringsalternativCommand } from "~app/features/planera/commands/MergeAllDestineringsalternativCommand";
import { TplDestineringModified } from "~app/features/transport/transportplanera/events/tplEventHandler";
import { ClearDestineringAndelArbetsordrarCommand, DestineraArbetsordrarCommand } from "~app/features/transport/transportplanera/models/commands";
import { IKeyMottagninsplatsValue, IKeyValue, ITplArbetsoderHandelssortimentDetails } from "~app/features/transport/transportplanera/models/dtos";
import { ITplArbetsorderDestinerbaraMottagningsplatserQueryResponse } from "~app/features/transport/transportplanera/models/queries";
import { TplStateService } from "~app/features/transport/transportplanera/state/tpl-state.service";
import { ValidationMessageRowComponent } from "../../../../../../../common/validation/validation-message-row/validation-message-row.component";
import { OmdestineraService } from "../../omdestinera.service";
import { ITplOmdestineraViewModel, ITplValidationMessage } from "../../tpl-omdestinera.models";

@Component({
    selector: "rask-tpl-destinera-modal",
    templateUrl: "./tpl-destinera-modal.component.html",
    styleUrls: ["./tpl-destinera-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        BlockComponent,
        FormsModule,
        ReactiveFormsModule,
        LabelComponent,
        InfoComponent,
        SelectorComponent,
        InputNumberComponent,
        ButtonComponent,
        ValidationMessageRowComponent,
        ActionButtonComponent,
        TranslatePipe
    ]
})
export class TplDestineraModalComponent implements OnInit {
    antalMottagningsplatser = 0;
    command: DestineraArbetsordrarCommand;
    destinerbaraMottagningsplatser: ITplArbetsorderDestinerbaraMottagningsplatserQueryResponse[];
    destineringarFormArray: UntypedFormArray;
    form: UntypedFormGroup;
    mottagningsplatser: IKeyValue[];
    summaVolym: number;
    targetArbetsorderList: ITplOmdestineraViewModel[];
    targetHandelssortiment: IKeyValue;
    validationContext: IValidationContext;
    validationMessages: ITplValidationMessage[] = [];

    protected isVaglagerFilterActive = false;

    private readonly activeModal = inject(NgbActiveModal);
    private readonly busyService = inject(BusyService);
    private readonly commandService: CommandService;
    private readonly commandServiceFactory = inject(CommandService);
    private readonly destineringService = inject(OmdestineraService);
    private readonly eventService = inject(EventService);
    private readonly formBinder = inject(FormBinderService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly toastService = inject(ToastService);
    private readonly tplStateService = inject(TplStateService);
    private readonly translateService = inject(TranslateService);

    constructor() {
        this.commandService = this.commandServiceFactory.createInstance();
    }

    get totalAndel(): number {
        if (!this.command) {
            return 0;
        }

        const summa = sumBy(this.command.destineringar, x => x.andel ?? 0);
        if (summa) {
            return summa;
        }

        if (this.command.destineringar.length === 1) {
            const andel = this.command.destineringar[0].andel;
            return andel === null ? 100 : andel;
        }

        return 0;
    }

    static open(
        modalService: NgbModal,
        targetArbetsorderList: ITplOmdestineraViewModel[],
        callBack: (result: any) => void = null, dialogSize?: string) {
        const options = {
            keyboard: false,
            backdrop: "static",
            centered: true,
            windowClass: "modal-size",
            size: dialogSize
        } as NgbModalOptions;
        const modalRef = modalService.open(this, options);
        modalRef.componentInstance.targetArbetsorderList = targetArbetsorderList;

        if (callBack) {
            modalRef.result.then(r => callBack(r));
            return;
        }
        modalRef.result.then(r => modalRef.close(r));
    }

    addDestinering(): void {
        this.command.destineringar.push({
            mottagningsplatsId: null,
            foradlatHandelssortimentId: null,
            mottagningsplatsRef: null,
            andel: null
        });

        this.initDestineringarFormArray(true);
        this.initValidation();
    }

    canAddDestinering(): boolean {
        return this.antalMottagningsplatser > this.command.destineringar.length && this.canDestinera();
    }

    canDestinera(): boolean {
        return this.targetArbetsorderList.some(x => !x.isExternArbetsorder);
    }

    canSave(): boolean {
        return this.form.dirty && this.form.valid && this.totalAndel === 100;
    }

    canSetAndel(): boolean {
        return this.command.destineringar.length > 1;
    }

    finalizeCommand() {
        this.command.destineringar.forEach(x => {
            if (ObjectUtils.hasValue(x.mottagningsplatsRef)) {
                const split = x.mottagningsplatsRef.split("/");
                x.mottagningsplatsId = +split[0].replace(/[$,]/g, "");
                x.foradlatHandelssortimentId = ObjectUtils.stringHasValue(split[1]) ? +split[1].replace(/[$,]/g, "") : null;
            }
        });
    }

    getDestineradeMottagningsplatser(selectedIdx: number): IKeyMottagninsplatsValue[] {
        const clone = [...this.command.destineringar];
        clone.splice(selectedIdx, 1);

        return clone
            .map(x => x.mottagningsplatsRef)
            .filter(x => !!x)
            .map(x => ({
                key: x,
                value: ""
            }));
    }

    initDestineringarFormArray(markAsDirty: boolean): void {
        const destineringFormGroups = this.command.destineringar.map(x => this.formBinder.CreateGroup(x));

        // "SetFormArray"
        this.destineringarFormArray.controls = [];
        destineringFormGroups.forEach(x => x.setParent(this.destineringarFormArray));

        this.destineringarFormArray.controls.push(...destineringFormGroups);

        if (markAsDirty) {
            this.form.markAsDirty();
            this.updateWarnings();
        }
    }

    initForm(command: DestineraArbetsordrarCommand): void {
        this.destineringarFormArray = new UntypedFormArray([]);

        this.form = new UntypedFormGroup({
            destineringar: this.destineringarFormArray
        });

        this.initDestineringarFormArray(false);

        this.form.valueChanges.subscribe(() => this.updateWarnings());
    }

    initMottagningsplatser(aoIdList: number[]): Observable<IKeyValue[]> {
        return this.destineringService
            .getDestinerbaraMottagningsplatser(aoIdList, this.targetHandelssortiment.key)
            .pipe(tap(x => this.antalMottagningsplatser = x.length))
            .pipe(tap(x => this.destinerbaraMottagningsplatser = x))
            .pipe(map(x => x.map(x1 => x1.key)));
    }

    initValidation(): void {
        const destineringar = this.command.destineringar;
        const context = new ValidationContext();
        context.errorLevel = ValidationErrorLevels.error;
        context
            .create("default")
            .forEach(destineringar, (destinering, scope, idx) => scope.withRules([
                {
                    message: { text: "Måste anges" },
                    model: () => destinering.mottagningsplatsId,
                    name: `mottagningsplats${idx}`,
                    required: true
                },
                {
                    message: { text: "Måste anges" },
                    applicable: () => destineringar.length > 1,
                    model: () => destinering.andel,
                    name: `andel${idx}`,
                    required: true
                }
            ]));

        this.formBinder.attachValidation(context.current, this.form);
        this.validationContext = context;
    }

    ngOnInit(): void {
        const aoIdList = this.targetArbetsorderList.map(x => x.id);
        const allHsInfo = this.targetArbetsorderList
            .map(x => x.handelssortimentInfo)
            .filter(x => !!x);

        this.isVaglagerFilterActive = this.tplStateService.current.getSelectedVaglager();

        if (!allHsInfo.length) {
            this.tryClose();
            this.toastService.add("Förväntades minst en arbetsorder med angiven handelssortimentsinformation.", 3, 5000);
            throw new Error("Förväntades minst en arbetsorder med angiven handelssortimentsinformation.");
        }

        this.targetHandelssortiment = {
            key: allHsInfo[0].handelssortimentId,
            value: allHsInfo[0].handelssortimentNamn
        };

        this.updateVolym(allHsInfo);

        this.command = new DestineraArbetsordrarCommand(aoIdList, this.targetHandelssortiment.key);

        this.initMottagningsplatser(aoIdList).subscribe(x => {
            this.mottagningsplatser = x;
        });
        this.initForm(this.command);
        this.initValidation();
    }

    remove(index: number): void {
        this.command.destineringar.splice(index, 1);
        this.initDestineringarFormArray(true);
        this.initValidation();
    }

    save(): void {
        this.finalizeCommand();
        if (!this.validationContext.validate(true)) {
            return;
        }

        if (this.command.destineringar.length === 1) {
            this.command.destineringar[0].andel = 100;
        }

        this.removeDisabled();

        this.commandService.addTransactionCommand(this.command);

        this.busyService
            .addWork(this.commandService.pushCommandQueueTransaction())
            .subscribe(x => {
                this.eventService.emit(new TplDestineringModified());
                this.toastService.add("Ändringarna sparades");
                this.activeModal.close(true);
            });
    }

    tplMottagningsplatsFormatter = (result: IKeyValue) => result ? result.value ?? "" : "";

    tplMottagningsplatsOutputFormatter = (result: IKeyValue) => result ? result.key : undefined;

    tryClose(): void {
        this.activeModal.close(false);
    }

    uppdateraDestineringsalternativ() {
        // Detta command körs alltid isolerat - oavsett vad som ligger i stacken hos commandService
        const local = this.commandService.createInstance();

        this.targetArbetsorderList.forEach(ao => {
            const command = new MergeAllDestineringsalternativCommand(x => x.arbetsorderId = ao.id);

            local.addTransactionCommand(command);
        });

        const work = local.pushCommandQueueTransaction();
        this.busyService.addWork(work).subscribe(x => {
            const aoIdList = this.targetArbetsorderList.map(ao => ao.id);
            this.initMottagningsplatser(aoIdList).subscribe(mp => {
                this.mottagningsplatser = mp;
                this.updateWarnings();
            });
        });
    }

    protected deleteDestineringar(): void {
        const dialogResponse$ = this.raskModalService.confirm(new ModalSettings(`Ta bort destinering för ${this.targetHandelssortiment.value}`,
            "Vill du ta bort destinering för valda arbetsordrar? Detta går inte att ångra!"));

        dialogResponse$.subscribe({
            next: (result) => {
                if (result === ConfirmDialogResponse.save) {
                    this.deleteDestineringarArbetsorder();
                }
            }
        });
    }

    private deleteDestineringarArbetsorder(): void {
        const cmdService = this.commandService.createInstance();

        const aoList = this.targetArbetsorderList.map(x => x.arbetsorderId);
        const hsId = this.targetHandelssortiment.key;
        const command = new ClearDestineringAndelArbetsordrarCommand(aoList, hsId);

        cmdService.addTransactionCommand(command);

        const work = cmdService.pushCommandQueueTransaction();

        this.busyService.addWork(work).subscribe(() => {
            this.toastService.add("Destineringar borttagna...");
            this.eventService.emit(new TplDestineringModified());
            this.activeModal.close("delete");
        });
    }

    private removeDisabled() {
        const mottagningsplatserIds = this.command.destineringar.map(x => x.mottagningsplatsId);

        mottagningsplatserIds.forEach(x => {
            this.destinerbaraMottagningsplatser.forEach(d => {
                if (d.key.key === x && d.inaktivaOnDokument.length > 0) {
                    d.inaktivaOnDokument.forEach(id => {
                        // eslint-disable-next-line sonarjs/no-nested-functions
                        const index = this.command.arbetsorderIdList.findIndex(a => a === id);
                        this.command.arbetsorderIdList.splice(index, 1);
                    });
                }
            });
        });
    }

    private updateVolym(hsInfo: ITplArbetsoderHandelssortimentDetails[]): void {
        if (this.isVaglagerFilterActive) {
            this.summaVolym = sumBy(hsInfo, x => x?.vagVolym ?? 0);
        } else {
            this.summaVolym = sumBy(hsInfo, x => x?.planeradVolym ?? 0);
        }
    }

    private updateWarnings() {
        this.validationMessages = [];

        const currentMottagningsplatsRefList = this.command.destineringar.map(x => x.mottagningsplatsRef);

        const affectedArbetsorderList = chain(this.destinerbaraMottagningsplatser)
            .filter(x => currentMottagningsplatsRefList.some(x1 => x1 === x.key.key.toString()))
            .flatMap(x => x.documentIdList)
            .uniq()
            .map(x => this.targetArbetsorderList.find(x1 => x1.id === x))
            .value();

        const hsInfoList = affectedArbetsorderList.map(x => x.handelssortimentInfo);
        this.updateVolym(hsInfoList);

        let notMatched: ITplOmdestineraViewModel[] = [];
        let inactive: ITplOmdestineraViewModel[] = [];

        if (affectedArbetsorderList.length > 0) {
            notMatched = differenceBy(this.targetArbetsorderList, affectedArbetsorderList, x => x.id);
            inactive = chain(this.destinerbaraMottagningsplatser)
                .filter(x => currentMottagningsplatsRefList.some(x1 => x1 === x.key.key.toString()))
                .flatMap(x => x.inaktivaOnDokument)
                .uniq()
                .map(x => this.targetArbetsorderList.find(x1 => x1.id === x))
                .value();
        }

        this.targetArbetsorderList.forEach(ao => {
            ao.hasWarning = false;
            ao.warningVisible = false;
            ao.warnings = [];
        });

        notMatched.forEach(ao => {
            ao.hasWarning = true;
            ao.warnings.push({
                brief: this.translateService.instant("TPL_DESTINERA_EJDESTINERAD_TOOLTIP"),
                long: this.translateService.instant("TPL_DESTINERA_EJDESTINERAD_DETAILS"),
                type: "warning"
            });
        });

        inactive.forEach(ao => {
            ao.hasWarning = true;
            ao.warnings.push({
                brief: "Arbetsordern kunde inte destineras eftersom det finns destineringsalternativ som begränsats",
                long: "Arbetsordern kunde inte destineras eftersom det finns destineringsalternativ som begränsats",
                type: "error"
            });
            ao.warningVisible = true;
        });

        if (notMatched.length) {
            this.validationMessages.push({
                text: `${notMatched.length} av ${this.targetArbetsorderList.length} går inte att destinera till vald(a) mottagningsplats(er)`,
                type: "warning"
            });
        }

        if (inactive.length) {
            this.validationMessages.push({
                text: `${inactive.length} av ${this.targetArbetsorderList.length} arbetsordrar går inte att destinera eftersom det här destineringsalternativet begränsats på dem`,
                type: "error"
            });
        }
    }
}
